import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { Box, Skeleton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { type FC, Fragment, type MouseEvent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useListCallbackRef } from "react-window";
import { VirtualizedList } from "../../../../components/UIToolkit/molecules/VirtualizedList";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useListScrollbarWidth } from "../../../../hooks/useListScrollbarWidth";
import { INK, NEUTRAL, SURFACE } from "../../../../theme/tokens";
import type { DepartmentLocation, Nurse, NurseRole, RecurringShift } from "../../../../utils/models";
import { LocationCellProvider } from "../../v2/Cells/LocationCellContext";
import type { ScheduleData, Section, SectionLocationMapReadOnly } from "../../v2/utils";
import { getDayIndexInCycle } from "../consecutiveDateMath";
import {
	CELL_ITEM_GAP,
	CELL_ITEM_HEIGHT,
	CELL_PADDING_Y,
	CYCLE_BLOCKS_SHOWN,
	getGridMinWidth,
	getGridTemplateColumns,
	MIN_ROW_HEIGHT,
	ROW_BORDER_HEIGHT,
	ROW_OVERSCAN_COUNT,
	SECTION_HEADER_HEIGHT,
} from "../consts";
import { ConsecutiveRow } from "./ConsecutiveRow";

type GridItem = { kind: "section"; section: Section; locationCount: number } | { kind: "row"; section: Section; scheduleData: ScheduleData };

type Props = {
	calculatedData: SectionLocationMapReadOnly | null;
	isCalculating: boolean;
	loadError: boolean;
	/** Cycle-aligned date shown in the first (Week 1, Day 1) cell. */
	cycleStartDate: DateTime;
	/** Department's cycle anchor — labels each day column via `(date - firstSunday) % cycleLength`. */
	firstSunday: DateTime;
	/** Day columns per block (= the selected period's cycle length). */
	daysPerBlock: number;
	/** Cycle length in days (= daysPerBlock) — the modulo base for the "Day N" labels. */
	cycleLength: number;
	/** Department timezone — the frame the "Day N" cycle math is evaluated in. */
	timezone: string;
	/** Today (dept timezone, start of day) — drives the current-day highlight. */
	today: DateTime;
	expandedGroups: Set<string>;
	handleSectionCollapseToggle: (section: Section) => void;
	departmentSpecificRoles: NurseRole[];
	uniqueSections: string[];
	refetchRecurringShifts: (options?: RefetchOptions) => Promise<QueryObserverResult<RecurringShift[], Error>>;
	/** Retries whichever load put the grid in its error state. */
	retryLoad: () => void;
	onShiftClick: (recurringShift: RecurringShift, event: MouseEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	onShiftKeyDown: (recurringShift: RecurringShift, event: React.KeyboardEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	handleOnAddClick: (clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	getNurseById: (id: number) => Nurse | undefined;
};

const columnHeaderCellSx = {
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	py: 0.5,
	borderRight: `1px solid ${NEUTRAL[200]}`,
} as const;

export const ConsecutiveGrid: FC<Props> = ({
	calculatedData,
	isCalculating,
	loadError,
	retryLoad,
	cycleStartDate,
	firstSunday,
	daysPerBlock,
	cycleLength,
	timezone,
	today,
	expandedGroups,
	handleSectionCollapseToggle,
	departmentSpecificRoles,
	uniqueSections,
	refetchRecurringShifts,
	onShiftClick,
	onShiftKeyDown,
	handleOnAddClick,
	getNurseById,
}) => {
	const { t } = useTranslation();
	const [list, setList] = useListCallbackRef(null);
	const todayISO = useMemo(() => today.toISODate(), [today]);
	const gridTemplateColumns = useMemo(() => getGridTemplateColumns(daysPerBlock), [daysPerBlock]);

	const items = useMemo<GridItem[]>(() => {
		// Flatten sections + rows into a single virtualization list; collapsed sections contribute only their header.
		if (!calculatedData) return [];
		const sections = (Object.keys(calculatedData) as Section[]).sort((a, b) => a.localeCompare(b));
		const flat: GridItem[] = [];
		for (const section of sections) {
			const rows = calculatedData[section] ?? [];
			if (rows.length === 0) continue;
			flat.push({ kind: "section", section, locationCount: rows.length });
			if (expandedGroups.has(section)) {
				for (const scheduleData of rows) flat.push({ kind: "row", section, scheduleData });
			}
		}
		return flat;
	}, [calculatedData, expandedGroups]);

	const scrollbarWidth = useListScrollbarWidth(list);

	const rowHeight = useCallback(
		(index: number): number => {
			// Deterministic per-row height: section headers are fixed; location rows fit the busiest cell.
			// itemCount = maxShifts + 1 for the always-present "+" button; must match ConsecutiveCell's layout.
			const item = items[index];
			if (!item || item.kind === "section") return SECTION_HEADER_HEIGHT;
			let maxShifts = 0;
			for (const block of item.scheduleData.weeklyShifts) {
				for (const day of block.days) maxShifts = Math.max(maxShifts, day.recurringShiftsInCell.length);
			}
			const itemCount = maxShifts + 1;
			const contentHeight = CELL_PADDING_Y + itemCount * CELL_ITEM_HEIGHT + (itemCount - 1) * CELL_ITEM_GAP + ROW_BORDER_HEIGHT;
			return Math.max(MIN_ROW_HEIGHT, contentHeight);
		},
		[items],
	);

	const renderItem = useCallback(
		(item: GridItem) => {
			if (item.kind === "section") {
				const isExpanded = expandedGroups.has(item.section);
				return (
					<Box
						onClick={() => handleSectionCollapseToggle(item.section)}
						data-testid={`consecutive-master-schedule-section-${item.section}`}
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							height: "100%",
							px: 1,
							cursor: "pointer",
							backgroundColor: NEUTRAL[100],
							color: INK.PRIMARY,
							fontWeight: 600,
							"&:hover": { backgroundColor: NEUTRAL[200] },
						}}
					>
						{isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
						<Typography component="span" sx={{ fontWeight: 600, fontSize: "14px" }}>
							{item.section || t("schedule.template.no_location")}
						</Typography>
						<Typography component="span" sx={{ fontSize: "12px", color: INK.SECONDARY }}>
							({item.locationCount})
						</Typography>
					</Box>
				);
			}
			return (
				<ConsecutiveRow
					scheduleData={item.scheduleData}
					gridTemplateColumns={gridTemplateColumns}
					todayISO={todayISO}
					departmentSpecificRoles={departmentSpecificRoles}
					uniqueSections={uniqueSections}
					refetchRecurringShifts={refetchRecurringShifts}
					onShiftClick={onShiftClick}
					onShiftKeyDown={onShiftKeyDown}
					handleOnAddClick={handleOnAddClick}
					getNurseById={getNurseById}
				/>
			);
		},
		[
			expandedGroups,
			handleSectionCollapseToggle,
			t,
			gridTemplateColumns,
			todayISO,
			departmentSpecificRoles,
			uniqueSections,
			refetchRecurringShifts,
			onShiftClick,
			onShiftKeyDown,
			handleOnAddClick,
			getNurseById,
		],
	);

	if (loadError && !calculatedData) {
		return (
			<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 6 }} data-testid="consecutive-master-schedule-load-error">
				<Typography color="error">{t("error.general")}</Typography>
				<SystemButton buttonVariant="secondary" onClick={retryLoad}>
					{t("Refresh")}
				</SystemButton>
			</Box>
		);
	}

	return (
		<LocationCellProvider>
			<Box sx={{ width: "100%", height: "100%", overflowX: "auto", overflowY: "hidden" }} data-testid="consecutive-master-schedule-grid">
				<Box sx={{ minWidth: getGridMinWidth(daysPerBlock), height: "100%", display: "flex", flexDirection: "column" }}>
					{/* Sticky two-tier header (rendered outside the virtual list). Animate the scrollbar-gutter
					    reservation so the columns glide rather than jump when the scrollbar appears/disappears. */}
					<Box sx={{ paddingRight: `${scrollbarWidth}px`, transition: "padding-right 150ms ease" }}>
						{/* Tier 1 — cycle-block banners. */}
						<Box sx={{ display: "grid", gridTemplateColumns: gridTemplateColumns, gap: "1px" }}>
							<Box sx={{ gridColumn: "1 / span 2" }} />
							{Array.from({ length: CYCLE_BLOCKS_SHOWN }, (_, blockIndex) => (
								<Fragment key={`banner-${blockIndex}`}>
									<Box
										sx={(theme) => ({
											gridColumn: `span ${daysPerBlock}`,
											textAlign: "center",
											fontWeight: 700,
											fontSize: "12px",
											textTransform: "uppercase",
											letterSpacing: "0.05em",
											color: theme.palette.primary.contrastText,
											backgroundColor: theme.palette.primary.main,
											py: 0.5,
										})}
									>
										{t(blockIndex === 0 ? "current.cycle" : "next.cycle")}
									</Box>
									{blockIndex < CYCLE_BLOCKS_SHOWN - 1 && <Box />}
								</Fragment>
							))}
						</Box>
						{/* Tier 2 — Location / Shift / Day columns. */}
						<Box sx={{ display: "grid", gridTemplateColumns: gridTemplateColumns, paddingTop: "2px", gap: "1px", backgroundColor: alpha(NEUTRAL[200], 0.4) }}>
							<Box sx={{ ...columnHeaderCellSx, alignItems: "flex-start", px: 1 }}>
								<Typography sx={{ fontSize: "12px", fontWeight: 700, color: INK.PRIMARY }}>{t("location")}</Typography>
							</Box>
							<Box sx={{ ...columnHeaderCellSx, alignItems: "flex-start", px: 1 }}>
								<Typography sx={{ fontSize: "12px", fontWeight: 700, color: INK.PRIMARY }}>{t("Shift")}</Typography>
							</Box>
							{Array.from({ length: CYCLE_BLOCKS_SHOWN }, (_, blockIndex) => (
								<Fragment key={`daycols-${blockIndex}`}>
									{Array.from({ length: daysPerBlock }, (_, dayIndex) => {
										const date = cycleStartDate.plus({ days: blockIndex * daysPerBlock + dayIndex });
										const isToday = date.toISODate() === todayISO;
										const dayLabel = getDayIndexInCycle({ date, firstSunday, cycleLength, timezone }) + 1;
										return (
											<Box
												key={date.toISO()}
												data-testid={`consecutive-master-schedule-col-header-${blockIndex}-${dayIndex}`}
												sx={(theme) => ({
													...columnHeaderCellSx,
													position: "relative",
													backgroundColor: isToday ? alpha(theme.palette.primary.main, 0.08) : SURFACE,
													...(isToday
														? {
																"&::before": {
																	content: '""',
																	position: "absolute",
																	top: 0,
																	left: 0,
																	right: 0,
																	height: "3px",
																	background: theme.palette.primary.main,
																},
															}
														: {}),
												})}
											>
												<Typography sx={{ fontSize: isToday ? "13px" : "11px", fontWeight: 700, color: isToday ? INK.PRIMARY : INK.SECONDARY }}>
													{t("Day")} {dayLabel}
												</Typography>
												<Typography sx={{ fontSize: "10px", color: INK.SECONDARY }}>{date.toFormat("MMM d")}</Typography>
											</Box>
										);
									})}
									{blockIndex < CYCLE_BLOCKS_SHOWN - 1 && <Box />}
								</Fragment>
							))}
						</Box>
					</Box>

					{/* Virtualized rows. */}
					{isCalculating || !calculatedData ? (
						<Box sx={{ display: "flex", flexDirection: "column", gap: "2px", pt: 1 }}>
							{Array.from({ length: 8 }, (_, index) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: fixed-count skeleton placeholders
								<Skeleton key={index} variant="rounded" height={MIN_ROW_HEIGHT} />
							))}
						</Box>
					) : (
						<VirtualizedList
							sx={{ flex: 1, minHeight: 0, width: "100%" }}
							items={items}
							rowHeight={rowHeight}
							renderItem={renderItem}
							overscanCount={ROW_OVERSCAN_COUNT}
							listRef={setList}
							itemWrapperSx={{ borderBottom: `1px solid ${NEUTRAL[200]}` }}
						/>
					)}
				</Box>
			</Box>
		</LocationCellProvider>
	);
};
